import * as React from 'react'
import { Dispatch, PropsWithChildren, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Button, Input, makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components'
import {
    ArrowMaximize24Filled,
    ArrowMaximize24Regular,
    ArrowMinimize24Filled,
    ArrowMinimize24Regular,
    bundleIcon,
    DismissFilled,
    DismissRegular,
    DismissSquareFilled,
    SearchFilled,
    SearchRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { BaseOverviewProps, useOverviewStyles } from './BaseOverview'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext, EntityDto } from '../../config/appConfig'
import { BaseOverviewSortingType, BaseSortMenu } from './BaseSortMenu'
import { Slot } from '@fluentui/react-utilities'
import { DateTime } from 'luxon'

const DismissIcon = bundleIcon(DismissFilled, DismissRegular)
const DismissSquareIcon = bundleIcon(DismissSquareFilled, DismissSquareFilled)
const FilterCollapseIcon = bundleIcon(ArrowMinimize24Filled, ArrowMinimize24Regular)
const FilterExpandIcon = bundleIcon(ArrowMaximize24Filled, ArrowMaximize24Regular)
const SearchIcon = bundleIcon(SearchFilled, SearchRegular)

const useTooltipStyles = makeStyles({
    inverted: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundInverted,
    },
})

export enum ViewType {
    ListView = 'LIST_VIEW',
    KanbanView = 'KANBAN_VIEW',
}

export type BaseOverviewFilterProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
> = BaseOverviewProps<EntityT, FilterT, SortingT> &
    PropsWithChildren<{
        filtersOpen: boolean
        setFiltersOpen: (value?: boolean) => void
        sortingValues?: SortingT
        setSortingValues?: Dispatch<SetStateAction<SortingT>>
        viewType?: ViewType
        setViewType?: Dispatch<SetStateAction<ViewType>>
    }>

export type BaseOverviewFilterType = {
    query?: string
    tag?: string
    icon?: Slot<'span'>
    title?: string
}

export function generateFromDate(tag: string): string {
    switch (tag) {
        case 'day':
        case 'today':
        case '7days':
            return DateTime.local().toISODate()
        case 'week':
            return DateTime.local().startOf('week').toISODate()
        case 'month':
            return DateTime.local().startOf('month').toISODate()
        case 'future':
            return DateTime.local().toISODate()
        default:
            return null
    }
}

export function generateToDate(tag: string): string {
    switch (tag) {
        case 'day':
        case 'history':
        case 'today':
            return DateTime.local().toISODate()
        case 'week':
            return DateTime.local().endOf('week').toISODate()
        case 'month':
            return DateTime.local().endOf('month').toISODate()
        case '7days':
            return DateTime.local().plus({ day: 6 }).toISODate()
        default:
            return null
    }
}

//https://stackoverflow.com/questions/66263176/sorting-object-properties-typing-errors
function sortProperties<T>(obj: T): T {
    return (Object.keys(obj) as Array<keyof T>).sort().reduce(<K extends keyof T>(accum: T, key: K) => {
        accum[key] = obj[key]
        return accum
    }, {} as T)
}

export default function BaseOverviewFilter<
    Entity extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
>(props: BaseOverviewFilterProps<Entity, FilterT, SortingT>) {
    const { values, setValues, setFieldValue } = useFormikContext<FilterT>()
    const styles = useOverviewStyles()
    const tooltipStyles = useTooltipStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const filterOutlookNew = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const filterInputRef = React.useRef<HTMLInputElement>(null)

    // Set the focus to the SearchBox upon the first render.
    useEffect(() => {
        setTimeout(() => {
            if (props.focusQueryOnRender !== false && document.hasFocus() && (!isOfficeInitialized() || !isAppContext([AppContext.Compose]))) {
                filterInputRef?.current?.focus()
            }
        }, 200)
    }, [])

    const isSettingsDefaults = useMemo(() => {
        const defaultJson = JSON.stringify(sortProperties<FilterT>(props.filterToJson?.(props.defaultFilterValues) || props.defaultFilterValues))
        const currentJson = JSON.stringify(sortProperties<FilterT>(props.filterToJson?.(values) || values))

        return defaultJson.localeCompare(currentJson) === 0
    }, [values])

    const [previousValues, setPreviousValues] = useState<FilterT>()
    const [queryValue, setQueryValue] = useState<string>(values.query)
    const [expandTooltipVisible, setExpandTooltipVisible] = useState<boolean>(false)

    useEffect(() => {
        //we've reached the minimum query length
        if (values.query?.length < 3 && queryValue?.length >= 3) {
            console.log('reached minimum query size, clearing...')
            setPreviousValues(values)
            setValues(values => ({ ...props.fixedFilterValues, statuses: values['statuses'], query: queryValue }))
            setExpandTooltipVisible(true)
        }
        //we've left the minimum query length
        else if (values.query?.length >= 3 && queryValue?.length < 3) {
            console.log('under minimum query size, setting previous...')
            setValues({
                ...(previousValues || props.filterFromJson?.(props.defaultFilterValues) || props.defaultFilterValues),
                query: '',
            })
            setExpandTooltipVisible(false)
        } else if (queryValue?.length >= 3) {
            setFieldValue('query', queryValue)
        }
    }, [queryValue])

    return (
        <>
            <div className={mergeClasses(styles.filterContainer, filterOutlookNew && styles.filterContainerOutlookNew)}>
                {!filterOutlookNew && <BaseSortMenu {...props} sortingValues={props.sortingValues} setSortingValues={props.setSortingValues} />}
                <Input
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    ref={filterInputRef}
                    placeholder={translate(props.inputPlaceholderKey || 'action.search')}
                    value={queryValue}
                    onChange={(_, data) => setQueryValue(data.value)}
                    contentBefore={
                        queryValue?.length <= 0 ? (
                            <SearchIcon />
                        ) : (
                            <Tooltip
                                content={{
                                    children: translate('base.filter.reset.query'),
                                    className: tooltipStyles.inverted,
                                }}
                                withArrow
                                relationship={'description'}
                                positioning={'below'}
                            >
                                <Button appearance={'transparent'} icon={<DismissIcon />} onClick={() => setQueryValue('')} />
                            </Tooltip>
                        )
                    }
                    contentAfter={
                        <>
                            <Tooltip
                                content={{
                                    children: translate('base.filter.reset'),
                                    className: tooltipStyles.inverted,
                                }}
                                withArrow
                                relationship={'description'}
                                positioning={'below'}
                            >
                                <Button
                                    appearance={'transparent'}
                                    className={mergeClasses(styles.colorRed, isSettingsDefaults && styles.hidden)}
                                    icon={<DismissSquareIcon />}
                                    onClick={() => {
                                        setValues({
                                            ...(props.filterFromJson?.(props.defaultFilterValues) || props.defaultFilterValues),
                                            query: '',
                                        }).then(() => setQueryValue(''))
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                content={{
                                    children: translate('base.filter.expand'),
                                    className: tooltipStyles.inverted,
                                }}
                                withArrow
                                relationship={'description'}
                                positioning={'below'}
                                visible={expandTooltipVisible}
                                onVisibleChange={() => setExpandTooltipVisible(!expandTooltipVisible)}
                            >
                                <Button
                                    appearance={'transparent'}
                                    icon={props.filtersOpen ? <FilterCollapseIcon /> : <FilterExpandIcon />}
                                    onClick={() => props.setFiltersOpen()}
                                />
                            </Tooltip>
                        </>
                    }
                />
                <div className={mergeClasses(styles.flexGap, styles.flexJustifyBetween)}>
                    {filterOutlookNew && <BaseSortMenu {...props} sortingValues={props.sortingValues} setSortingValues={props.setSortingValues} />}
                    <div className={styles.flexGap}>{props.headerActionsButtons && <props.headerActionsButtons {...props} />}</div>
                </div>
            </div>
            {props.headerDefaultFilterTags && (
                <div
                    className={mergeClasses(styles.filterContainer, styles.filterTagContainer, filterOutlookNew && styles.filterContainerOutlookNew)}
                >
                    <div></div>
                    <props.headerDefaultFilterTags {...props} />
                </div>
            )}
            <div
                className={mergeClasses(
                    styles.filterContainer,
                    filterOutlookNew && styles.filterContainerOutlookNew,
                    !props.filtersOpen && styles.hidden,
                )}
            >
                <div></div>
                <div className={styles.filtersContainer}>{props.children}</div>
                <div></div>
            </div>
        </>
    )
}
